import React from 'react';
import { Search, X } from 'lucide-react';

interface Filters {
  type: 'all' | 'pdf' | 'excel' | 'csv';
  search: string;
}

interface DocumentFiltersProps {
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
}

const DocumentFilters: React.FC<DocumentFiltersProps> = ({ filters, onFilterChange }) => {
  const filterTypes: Array<{ value: Filters['type']; label: string }> = [
    { value: 'all', label: 'All Types' },
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel' },
    { value: 'csv', label: 'CSV' },
  ];

  const handleTypeChange = (type: Filters['type']) => {
    onFilterChange({ ...filters, type });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, search: e.target.value });
  };

  const handleClearFilters = () => {
    onFilterChange({ type: 'all', search: '' });
  };

  const hasActiveFilters = filters.type !== 'all' || filters.search !== '';

  return (
    <div className="flex items-center justify-between gap-4 pb-4 border-b border-gray-200">
      {/* File type filter buttons */}
      <div className="flex items-center gap-2">
        {filterTypes.map((filterType) => (
          <button
            key={filterType.value}
            onClick={() => handleTypeChange(filterType.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filters.type === filterType.value
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filterType.label}
          </button>
        ))}
      </div>

      {/* Search input and clear filters */}
      <div className="flex items-center gap-2">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={filters.search}
            onChange={handleSearchChange}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-64"
          />
        </div>

        {/* Clear filters button */}
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            title="Clear filters"
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-4 h-4" />
            <span>Clear</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default DocumentFilters;
